// apps/web/src/providers/auth-provider.tsx
// Authentication context and provider

import * as React from 'react'
import { useCurrentUser, useLogout } from '@/hooks/useAuth'
import type { AuthUser, UserRole } from '@/lib/api-client'

interface AuthContextValue {
  user: AuthUser | null | undefined
  isLoading: boolean
  isAuthenticated: boolean
  logout: () => void
  hasRole: (roles: UserRole | UserRole[]) => boolean
  hasBranchAccess: (branchId: number) => boolean
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isPending: isLoading, isError } = useCurrentUser()
  const logoutMutation = useLogout()

  const isAuthenticated = !isLoading && !isError && !!user

  const hasRole = React.useCallback(
    (roles: UserRole | UserRole[]) => {
      if (!user) return false
      const roleArray = Array.isArray(roles) ? roles : [roles]
      return roleArray.includes(user.role)
    },
    [user]
  )

  const hasBranchAccess = React.useCallback(
    (branchId: number) => {
      if (!user) return false
      if (user.hasAllBranchAccess) return true
      return user.allowedBranchIds.includes(branchId)
    },
    [user]
  )

  const logout = React.useCallback(() => {
    logoutMutation.mutate()
  }, [logoutMutation])

  const value = React.useMemo(
    () => ({
      user: isError ? null : user,
      isLoading,
      isAuthenticated,
      logout,
      hasRole,
      hasBranchAccess,
    }),
    [user, isLoading, isAuthenticated, isError, logout, hasRole, hasBranchAccess]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Component to protect routes that require authentication
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-page">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-muted">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Will be handled by router redirect
    return null
  }

  return <>{children}</>
}

/**
 * Component to show content only for specific roles
 */
export function RoleGate({
  roles,
  children,
  fallback = null,
}: {
  roles: UserRole | UserRole[]
  children: React.ReactNode
  fallback?: React.ReactNode
}) {
  const { hasRole } = useAuth()

  if (!hasRole(roles)) {
    return <>{fallback}</>
  }

  return <>{children}</>
}
